import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowDownRight } from "lucide-react";

const lines = ["Artisanal", "gelato, born", "in Bengaluru"];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.5 + i * 0.14 },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, reduce ? 1 : 1.12]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-screen pt-28 pb-16 px-6 lg:px-10 overflow-hidden bg-bgpink"
      data-testid="hero-section"
    >
      {/* squiggle decor */}
      <svg className="absolute -right-20 top-40 w-[420px] opacity-40 hidden lg:block" viewBox="0 0 200 200" fill="none" aria-hidden="true">
        <path d="M10 100 Q 50 40 90 100 T 170 100" stroke="#A3B18A" strokeWidth="8" fill="none" strokeLinecap="round"/>
        <path d="M10 130 Q 50 70 90 130 T 170 130" stroke="#C98B9E" strokeWidth="8" fill="none" strokeLinecap="round"/>
      </svg>

      <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-10 items-center min-h-[calc(100vh-11rem)]">
        {/* Left — kinetic heading */}
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="uppercase tracking-[0.35em] text-xs font-500 text-cocoa/60 mb-8 flex items-center gap-3"
            data-testid="hero-overline"
          >
            <span className="h-px w-10 bg-cocoa/40" /> Est. Bengaluru · Small batch
          </motion.p>

          <h1 className="font-heading font-600 text-cocoa leading-[0.95] tracking-tight text-5xl sm:text-6xl lg:text-[5.5rem]">
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden py-[0.05em]">
                <motion.span
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="visible"
                  className="block"
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15 }}
            className="mt-8 max-w-md text-base sm:text-lg text-cocoa/70 leading-relaxed"
            data-testid="hero-subtext"
          >
            Slow-churned in tiny batches with real fruit, Belgian chocolate
            and nothing artificial. This is gelato with a point of view.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.35 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#flavors"
              data-testid="hero-cta-button"
              className="group inline-flex items-center gap-2 rounded-full bg-cocoa text-vanilla px-8 py-4 text-sm font-500 hover:bg-berry transition-colors duration-300"
            >
              Explore our flavours
              <ArrowDownRight size={18} className="group-hover:rotate-45 transition-transform duration-300" />
            </a>
            <a
              href="#about"
              data-testid="hero-secondary-link"
              className="inline-flex items-center rounded-full border border-cocoa/25 px-8 py-4 text-sm font-500 text-cocoa hover:bg-oat transition-colors duration-300"
            >
              Our story
            </a>
          </motion.div>
        </div>

        {/* Right — parallax pill image */}
        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.6 }}
            className="relative mx-auto w-full max-w-[420px] aspect-[3/4] rounded-full overflow-hidden ribbed border border-cocoa/10 shadow-2xl shadow-cocoa/10"
            data-testid="hero-image"
          >
            <motion.img
              src="/flavors/hero.png"
              alt="A hand holding an Odd Scoops cup of pistachio and mango gelato"
              style={{ y: imgY, scale: imgScale }}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
